import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScaleRangeStepper } from "components/__haim/Common/ScaleStepper";

export type NumberRangePickerProps = {
  /** Ascending list of allowed values (rooms, parking spots, …). */
  options: Readonly<number[]>;
  initialMin?: number;
  initialMax?: number;
  disabled?: boolean;
  minLabel?: string;
  maxLabel?: string;
  onRangeChange?: (min: number | undefined, max: number | undefined) => void;
  customOptionLabel?: (newNumber: number) => string | number;
};

/**
 * Generic numeric min/max range as +/− steppers over an arbitrary scale.
 *
 * Same control and same non-invertible guarantee as RoomRangePicker; this one
 * just takes its scale from the caller (rooms, parking spots, …). `undefined`
 * on either bound means "any" and is always reachable — clicking the value
 * clears it.
 */
export default function NumberRangePicker({
  options,
  initialMin,
  initialMax,
  disabled,
  minLabel,
  maxLabel,
  onRangeChange,
  customOptionLabel
}: NumberRangePickerProps) {
  const { t } = useTranslation();
  const [range, setRange] = useState<{ min: number | null; max: number | null }>({
    min: initialMin ?? null,
    max: initialMax ?? null
  });

  const handleChange = (min: number | null, max: number | null): void => {
    setRange({ min, max });
    onRangeChange?.(min ?? undefined, max ?? undefined);
  };

  return (
    <ScaleRangeStepper
      scale={options as number[]}
      min={range.min}
      max={range.max}
      disabled={disabled}
      minLabel={minLabel ?? t("listingsMatch.filters.minRoomsLabel", { defaultValue: "מ־" })}
      maxLabel={maxLabel ?? t("listingsMatch.filters.maxRoomsLabel", { defaultValue: "עד" })}
      formatValue={customOptionLabel}
      onChange={handleChange}
    />
  );
}
